import type {
  ConciseWorkspace,
  ConciseWorkspaces,
} from '@festina/configuration-general-types/configuration'
import { ConfigurationRepositoryWorkspacesService } from '@festina/configuration-general-types/configuration/services/ConfigurationRepositoryWorkspacesService'
import { type ConfigurableServer, notEmpty } from '@festina-life/core'
import { useSuspenseQuery } from '@tanstack/react-query'

import * as configurationKeys from '../keys/configuration'

export type AllConciseWorkspaces = Partial<
  Record<ConfigurableServer, ConciseWorkspace[]>
>

export const fetchConciseWorkspaces = async (
  servers: readonly ConfigurableServer[],
): Promise<AllConciseWorkspaces> => {
  const fetchAll = servers.map((server) =>
    ConfigurationRepositoryWorkspacesService.workspaceServiceGetGetConciseWorkspaces(
      { componentName: server.toLocaleLowerCase() },
    ),
  )

  const result = await Promise.allSettled(fetchAll)

  return Object.fromEntries(
    servers.map(
      (server, serverIndex) =>
        [
          server,
          result[serverIndex].status === 'fulfilled'
            ? (
                (
                  result[
                    serverIndex
                  ] as PromiseFulfilledResult<ConciseWorkspaces>
                ).value?.workspaces ?? []
              ).filter(notEmpty)
            : [],
        ] as const,
    ),
  ) as AllConciseWorkspaces
}

/**
 * Fetches the concise workspaces that exist for a user and a list of servers.
 */
export const useFetchAllConciseWorkspaces = ({
  servers,
  enabled,
}: {
  servers: readonly ConfigurableServer[]
  enabled: boolean
}) =>
  useSuspenseQuery({
    queryKey: enabled
      ? configurationKeys.fetchAllConciseWorkspacesKey(servers)
      : [],
    queryFn: () => {
      if (!enabled) {
        return Promise.resolve(null)
      }

      return fetchConciseWorkspaces(servers)
    },
  })
